<template>
  <div
    class="product-gallery-zoom"
    :class="{ 'is-zoom-active': states.zoomActive }"
  >
    <div
      v-if="images"
      class="product-gallery-zoom__wrapper"
    >
      <ul class="product-gallery-zoom__content">
        <li
          v-for="(image, index) in images"
          :key="`${image.id}-${index}-${image.variant}-Zoom`"
          class="product-gallery-zoom__image-holder"
          :data-image-index="index"
          js-product-gallery-zoom="observed"
          :js-product-gallery-zoom-image="index"
        >
          <div
            class="product-gallery-zoom__desktop-image"
            @click="handleZoomClick(image)"
          >
            <responsive-image
              block-class="product-gallery-zoom"
              :image="image"
            />
          </div>
        </li>
      </ul>

      <div
        js-product-gallery-zoom="viewport"
        class="product-gallery-zoom__viewport"
        :class="{
          [$class('active')]: states.zoomActive
        }"
      >
        <img
          class="product-gallery-zoom__zoom-image"
          js-product-gallery-zoom="image"
          :src="currentImageSrc?.src"
        >
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product gallery zoom (product-gallery-zoom)
 * -----------------------------------------------------------------------------
 * A carousel component for the Product Gallery displaying zoomed-in images.
 * @param {Number} currentIndex - default/current images index for the carousel.
 * @param {Array} [images] - Array of images from a formatted product object.
 * @param {Boolean} modalActive - Overlay drawer is active.
 *
 */
import PinchZoom from 'pinch-zoom-js'
import { nextTick } from 'vue'
import { mapGetters, mapState } from 'vuex'

import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'ProductGalleryZoom',

  components: {
    ResponsiveImage,
  },

  props: {
    currentIndex: {
      type: Number,
      default: 0,
    },
    images: {
      type: [Boolean, Array],
      default: false,
    },
    modalActive: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update-gallery-index'],

  data() {
    return {
      currentImageSrc: null,
      focussedElement: null,
      pinchZoom: false,
      states: {
        zoomActive: false,
        zoomProcess: 'ready',
      },
      viewedIndex: 0,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
     ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  watch: {

    /**
     * Destroy the pinch zoom when the modal is closed.
     * @param {String} - Overlay namespace.
     */
    getActiveOverlay(overlay) {
      if(overlay) {
        return
      }

      this.destroyPinchZoom(true)
    },

    /**
     * Watch the active state of the modal.
     */
    modalActive() {
      if (this.modalActive) {
        return
      }

      this.states.zoomActive = false
    },

    /**
     * Update the parent component when a new modal image is viewed.
     */
    viewedIndex() {
      if(!this.getActiveOverlay) {
        return
      }
      this.$emit('update-gallery-index', this.viewedIndex)
    },
  },

  beforeUpdate() {

    /**
     * Scroll to the selected image but only when the modal is reopened.
     */
    if(this.pinchZoom) {
      return
    }

    if(this.states.zoomProcess === 'closing') {
      this.states.zoomProcess = 'ready'
      return
    }

    this.scrollToImage(this.currentIndex)
  },

  mounted() {
    setTimeout(
      () => {
        this.scrollToImage(this.currentIndex)
      }, this.$timing('snappy'),
    )
    this.setImageIntersectionObservers()
  },

  methods: {

    /**
     * Handle clicks on the images to open pinch zoom on mobile.
     * @param {Number} index - Index of the image that was clicked.
     */
    handleZoomClick(element) {
      if(this.states.zoomActive || !this.isMobile) {
        return
      }

      this.currentImageSrc = element
      this.setPinchZoom()
      this.states.zoomActive = true
    },

    /**
     * Scrolls the zoomed view to the current index.
     */
    scrollToImage(index) {
      const image = document.querySelector(`[js-product-gallery-zoom-image=${CSS.escape(index)}]`)

      if(!image){
        return
      }

      /**
       * Needs a miniscule delay before trying to scroll, not sure why but this
       * resolves the scroll not triggering on component update.
       */
      setTimeout(() => {
        image.scrollIntoView(
          {
            block: "start",
            inline: "nearest",
            behaviour: 'smooth',
          },
        )
      }, 50)
    },

    /**
     * Gets the currently viewed image to set as the current index.
     */
    setImageIntersectionObservers() {
      const self = this
      const images = [...document.querySelectorAll('[js-product-gallery-zoom="observed"]')]

      const observerOptions = {
        rootMargin: '-50% 0% -50% 0%',
        threshold: 0,
      }

      const observer = new IntersectionObserver(observerCallback, observerOptions)

      function observerCallback(entries) {
      entries.forEach(entry => {
          if(entry.isIntersecting) {
            self.focussedElement = entry.target
            self.viewedIndex = entry.target.dataset.imageIndex
          }
        })
      }

      images.forEach(image => {
          observer.observe(image)
        })
      },

    /**
     * Initiate a new instance of PinchZoom.
     * - Library will initiate a new instance on a given active slide
     * - Update the state with zoomed status to prevent carousel dragging
     * @param {HTMLElement} element - container element for pinching.
     */
    setPinchZoom() {
      nextTick(() => {
        const element = document.querySelector('[js-product-gallery-zoom="image"]')
        this.pinchZoom = new PinchZoom(element, {
          animationDuration: 50,
          minZoom: 1,
          setOffsetsOnce: true,
          use2d: false,

          /**
           * User has tapped to initiate zoomed in/out state.
           */
          onDoubleTap: () => {
            this.destroyPinchZoom()
          },
        })
      })
    },

    /**
     * Cleans up when a pinch zoom window is closed.
     * @param {Boolean} nullify - Whether to clear the property in data also.
     */
    destroyPinchZoom(nullify = false){
      this.states.zoomActive = false
      this.currentImageSrc = null

      if(!this.pinchZoom){
        return
      }
      this.pinchZoom.disable()

      if(!nullify){
        return
      }
      this.pinchZoom = null
    },
  },
}
</script>

<style lang="scss">
@import './product-gallery-zoom';
</style>
